import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  createPrivateTemplate,
  deletePrivateTemplate,
  listPrivateTemplates,
  renamePrivateTemplate,
  type PrivateTemplate
} from '@peruselab/api-client/templates';
import type { DrawingDocument } from '@peruselab/structure-editor/model';
import { copySelectionDocument } from '@peruselab/structure-editor/domain/selectionTemplate';
import { hasSelection } from '@peruselab/structure-editor/domain/editor';
import type { UseDrawingDocumentResult } from '../../hooks/useDrawingDocument';
import { useAuth } from '../../auth/useAuth';
import { useActiveTenant } from '../../tenancy/activeTenant';
import { Icon } from '../../components/Icon';
import { SaveDrawingDialog } from './SaveDrawingDialog';
import { SelectionPlacementDialog } from './SelectionPlacementDialog';

export function SelectionTemplates({ doc }: { readonly doc: UseDrawingDocumentResult }) {
  const { user, isLoading } = useAuth();
  // #1051: the shelf is the active tenant's -- an organisation's members share theirs.
  // The personal shelf keeps its original cache key (the user id).
  const { active: activeTenant, scopeKey } = useActiveTenant();
  const owner = user?.profile.sub ? (activeTenant ? scopeKey : user.profile.sub) : null;
  const activeOwner = useRef(owner);
  useLayoutEffect(() => {
    activeOwner.current = owner;
  }, [owner]);
  const [library, setLibrary] = useState<{ owner: string | null; rows: readonly PrivateTemplate[] }>({
    owner: null,
    rows: []
  });
  const templates = library.owner === owner ? library.rows : [];
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<{ document: DrawingDocument } | { template: PrivateTemplate } | null>(null);
  const [placing, setPlacing] = useState<{ payload: DrawingDocument; base: DrawingDocument } | null>(null);
  const storageKey = `peruselab.selectionTemplates.${owner === null ? 'anonymous' : `account.${encodeURIComponent(owner)}`}`;
  const update = useCallback(
    (rows: readonly PrivateTemplate[]) => {
      setLibrary({ owner, rows });
      try {
        localStorage.setItem(storageKey, JSON.stringify(rows));
      } catch {
        /* In-memory fallback. */
      }
    },
    [owner, storageKey]
  );
  useEffect(() => {
    let active = true;
    setEditing(null);
    setPlacing(null);
    setError(null);
    try {
      const cached: unknown = JSON.parse(localStorage.getItem(storageKey) ?? '[]');
      setLibrary({
        owner,
        rows: Array.isArray(cached)
          ? cached.filter(
              (item): item is PrivateTemplate =>
                item &&
                typeof item === 'object' &&
                typeof item.id === 'string' &&
                typeof item.name === 'string' &&
                item.document &&
                typeof item.document === 'object'
            )
          : []
      });
    } catch {
      setLibrary({ owner, rows: [] });
    }
    setLoading(Boolean(owner));
    if (owner)
      void listPrivateTemplates()
        .then((rows) => {
          if (active) update(rows);
        })
        .catch(() => {
          if (active) setError('Account templates are unavailable. Cached templates can still be inserted.');
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    return () => {
      active = false;
    };
  }, [owner, storageKey, update]);
  const startSave = () => {
    try {
      setEditing({ document: copySelectionDocument(doc.document, doc.selection) });
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not copy the selection.');
    }
  };
  const remove = async (template: PrivateTemplate) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (owner) await deletePrivateTemplate(template.id);
      if (owner === activeOwner.current) update(templates.filter((item) => item.id !== template.id));
    } catch {
      if (owner === activeOwner.current) setError('Could not delete this template. Please retry.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="selection-templates" aria-label="Selection templates">
      <button
        type="button"
        className="selection-template-save"
        onClick={startSave}
        disabled={!hasSelection(doc.selection) || doc.hasMarkushContent || isLoading || loading || busy}
        title="Save selection as template"
      >
        <Icon name="favoriteOutline" size="1rem" /> <span>Save selection as template</span>
      </button>
      {!owner ? (
        <p className="workspace-placeholder">
          Templates saved here stay in this browser. Sign in to save private account templates.
        </p>
      ) : null}
      {error ? (
        <p className="import-error" role="alert">
          {error}
        </p>
      ) : null}
      <ul className="selection-template-list">
        {templates.map((template) => (
          <li key={template.id}>
            <button
              type="button"
              disabled={busy || loading}
              title={`By ${template.authorName}`}
              onClick={() => {
                try {
                  copySelectionDocument(template.document, {
                    atomIds: template.document.structure.graph.nodes.map((node) => node.id),
                    bondIds: [],
                    annotationIds: template.document.annotations.map((item) => item.id)
                  });
                  setPlacing({ payload: template.document, base: doc.document });
                } catch {
                  setError('This cached template is invalid. Reload the shelf.');
                }
              }}
            >
              {template.name}
            </button>
            <button
              type="button"
              aria-label={`Rename ${template.name}`}
              disabled={busy || loading}
              onClick={() => setEditing({ template })}
            >
              <Icon name="edit" size="1rem" />
            </button>
            <button
              type="button"
              aria-label={`Delete ${template.name}`}
              disabled={busy || loading}
              onClick={() => void remove(template)}
            >
              <Icon name="delete" size="1rem" />
            </button>
          </li>
        ))}
      </ul>
      {editing
        ? createPortal(
            <SaveDrawingDialog
              initialTitle={'template' in editing ? editing.template.name : ''}
              heading={'template' in editing ? 'Rename template' : 'Save selection as template'}
              saveLabel={'template' in editing ? 'Rename' : 'Save template'}
              maxLength={120}
              error={error}
              onClose={() => setEditing(null)}
              onSave={async (name) => {
                const account = owner;
                setError(null);
                try {
                  let saved: PrivateTemplate;
                  if ('template' in editing) {
                    saved = owner
                      ? await renamePrivateTemplate(editing.template, name)
                      : { ...editing.template, name, revision: editing.template.revision + 1 };
                  } else {
                    saved = owner
                      ? await createPrivateTemplate(name, editing.document)
                      : {
                          id: crypto.randomUUID(),
                          name,
                          document: editing.document,
                          authorId: 'browser',
                          authorName: 'You (this browser)',
                          revision: 1
                        };
                  }
                  if (activeOwner.current !== account) return false;
                  update([saved, ...templates.filter((item) => item.id !== saved.id)]);
                  return true;
                } catch (caught) {
                  if (activeOwner.current === account)
                    setError(caught instanceof Error ? caught.message : 'Could not save template.');
                  return false;
                }
              }}
            />,
            document.body
          )
        : null}
      {placing
        ? createPortal(
            <SelectionPlacementDialog
              base={placing.base}
              payload={placing.payload}
              onCancel={() => setPlacing(null)}
              onPlace={(placed) => {
                if (doc.document !== placing.base) {
                  setError('The drawing changed. Insert the template again.');
                  setPlacing(null);
                  return;
                }
                doc.commitDocument(() => placed.document);
                doc.setSelection(placed.selection);
                setPlacing(null);
              }}
            />,
            document.body
          )
        : null}
    </section>
  );
}
